import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { hapticButton } from "@/utils/haptics";

/**
 * Button — Luminous Atrium variants.
 *
 * Defaults to a generously-rounded surface that matches the Atrium design system.
 * Use `variant="cta"` for the primary forest-green pill (marketing & primary actions),
 * `variant="default"` for inverted dark pills, `variant="outline"` for hairline secondary,
 * `variant="ghost"` for low-emphasis chrome.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 touch-manipulation",
  {
    variants: {
      variant: {
        // Atrium primary — forest pill CTA. Use for the most important action on a surface.
        cta: "rounded-full bg-primary text-primary-foreground hover:shadow-[0_16px_40px_-12px_hsl(var(--primary)/0.45)] hover:-translate-y-0.5",
        // Inverted dark pill — secondary "important" actions
        default: "rounded-full bg-foreground text-background hover:bg-foreground/90 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_-12px_rgba(21,34,27,0.35)]",
        // Hairline outline — secondary actions on cream surfaces
        outline: "rounded-full border border-foreground/15 bg-background/60 backdrop-blur text-foreground hover:bg-foreground hover:text-background hover:border-foreground hover:-translate-y-0.5",
        // Low-emphasis filled — utility chrome (filters, toggles)
        secondary: "rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/70",
        // Destructive pill
        destructive: "rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:-translate-y-0.5",
        // Quiet chrome
        ghost: "rounded-full text-foreground/75 hover:bg-secondary hover:text-foreground",
        // Inline link
        link: "text-foreground underline-offset-4 hover:underline rounded-md",
        // Glass — translucent over imagery
        glass: "rounded-full bg-card/70 backdrop-blur border border-border/60 text-foreground hover:bg-card hover:-translate-y-0.5",
        // Legacy alias kept for back-compat (was forest stamp)
        stamp: "rounded-full bg-primary text-primary-foreground hover:shadow-[0_16px_40px_-12px_hsl(var(--primary)/0.45)] hover:-translate-y-0.5",
      },
      size: {
        default: "h-11 px-5 py-2.5 min-w-[44px]",
        sm: "h-9 px-4 text-[13px] min-w-[44px]",
        lg: "h-[52px] px-7 text-[15px] min-w-[44px]",
        xl: "h-[56px] px-8 text-[15px] min-w-[44px]",
        icon: "h-10 w-10 min-h-[44px] min-w-[44px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, onClick, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      hapticButton();
      onClick?.(e);
    };

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        onClick={handleClick}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
